/*
  Wheels & Tires hub behavior.

  This file owns exactly what is new on the hub: the Year/Make/Model cascade
  that turns a car into a bolt pattern, the cookie write that lets that car
  survive to the next page, and the tile marker that says which of the six
  patterns is theirs.

  Everything else is borrowed, not forked. The instrument primitives (the
  branded option panel, the saved-vehicle read, the token index and the
  ratified vehicle-to-spec lookup) come from assets/saikospeed-home.js through
  window.SaikoUI, and the spec bar itself is that file's saiko-wheel-bar
  element with its markup contract unchanged. Deferred scripts execute in
  document order and layout/theme.liquid loads that file first on this
  template, so the namespace is always present.

  THE COOKIE WRITE LIVES HERE, NOT THERE, ON PURPOSE. The home hero must never
  write hrllc_vehicle and a contract test enforces that against
  saikospeed-home.js by file. This is a different surface with the opposite
  requirement: a shopper who names their car on the wheels hub is about to
  leave for a pattern collection, and the answer has to travel with them.
*/
(function () {
  'use strict';

  var UI = window.SaikoUI;
  if (!UI || typeof document === 'undefined') return;

  var COOKIE_NAME = 'hrllc_vehicle';
  var COOKIE_DAYS = 30;

  /*
    Field for field the shape hrllc-grid-v1.js persistSelection writes, so the
    browse surfaces and the PDP read it back as their own. trim and
    fitment_code stay empty exactly as the browse cascade leaves them when no
    trim level is offered: the convermax rows bake the submodel into the model
    string ("Civic Si"), so the model alone is the whole identity.
  */
  function writeVehicleCookie(selection) {
    var expiry = new Date();
    expiry.setTime(expiry.getTime() + COOKIE_DAYS * 24 * 60 * 60 * 1000);
    document.cookie = COOKIE_NAME + '=' + encodeURIComponent(JSON.stringify({
      year: selection.year,
      make: selection.make,
      model: selection.model,
      trim: '',
      fitment_code: ''
    })) + ';expires=' + expiry.toUTCString() + ';path=/;SameSite=Lax';
  }

  function clearVehicleCookie() {
    document.cookie = COOKIE_NAME + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Lax';
  }

  function uniqueSorted(values, numeric) {
    var seen = {};
    var out = [];
    values.forEach(function (value) {
      if (!value || seen[value]) return;
      seen[value] = true;
      out.push(value);
    });
    out.sort(function (a, b) {
      if (numeric) return parseInt(b, 10) - parseInt(a, 10);
      return a.localeCompare(b);
    });
    return out;
  }

  /*
    Hands the resolved bolt pattern to the shipped spec bar by setting its own
    <select> and firing change, which is the same path a shopper's click takes.
    The option list arrives with the generated asset, which is fetched, so the
    pattern can resolve before the options exist. It retries briefly and then
    gives up rather than leaving a half-set bar: the state line has already
    told them what their car takes either way.
  */
  function setSpecBarPattern(pattern) {
    var select = document.querySelector('saiko-wheel-bar [data-saiko-wheelseg="pattern"] select');
    if (!select) return;
    var tries = 0;
    (function attempt() {
      var found = Array.prototype.some.call(select.options, function (option) {
        return option.value === pattern;
      });
      if (found) {
        if (select.value === pattern) return;
        select.value = pattern;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      tries += 1;
      if (tries > 40) return;
      window.setTimeout(attempt, 100);
    })();
  }

  function clearSpecBarPattern() {
    var select = document.querySelector('saiko-wheel-bar [data-saiko-wheelseg="pattern"] select');
    if (!select || !select.value) return;
    select.value = '';
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* ---------- the six tiles: which one is theirs ---------- */

  function paintTiles(vehicle) {
    var tiles = document.querySelectorAll('[data-saiko-hub-patterns] .saiko-hub-tile');
    if (!tiles.length) return;
    var spec = vehicle ? UI.resolveVehicleSpec(vehicle) : null;
    var pattern = spec && spec.p ? spec.p : '';
    Array.prototype.forEach.call(tiles, function (tile) {
      var fits = tile.querySelector('[data-saiko-hub-fits]');
      var match = Boolean(pattern) && tile.getAttribute('data-pattern') === pattern;
      tile.classList.toggle('is-match', match);
      // The row is reserved in the markup, so this only ever fills or empties
      // it: hiding the node would move every tile below it.
      if (fits) fits.textContent = match ? 'Fits your ' + UI.vehicleLabel(vehicle) : '';
    });
  }

  /* ---------- the vehicle cascade ---------- */

  if (typeof HTMLElement === 'function' && !customElements.get('saiko-hub-vehicle')) {
    customElements.define('saiko-hub-vehicle', class extends HTMLElement {
      connectedCallback() {
        UI.whenReady(this.setup.bind(this));
      }

      setup() {
        if (this.dataset.saikoInit === 'true') return;
        this.dataset.saikoInit = 'true';

        var island = this.querySelector('[data-saiko-hub-tokens]');
        var tokens = [];
        try {
          tokens = island ? JSON.parse(island.textContent || '[]') : [];
        } catch (error) {
          tokens = [];
        }
        this.rows = UI.indexTokens(tokens);
        this.selection = { year: '', make: '', model: '' };
        this.touched = false;

        /*
          A vehicle already saved elsewhere in the store is the answer to step
          one, so the page opens with it rather than asking again.

          IT IS ALSO KEPT SEPARATELY. The cascade can only offer vehicles the
          catalog carries parts for, so a saved car with no parts in the
          catalog drops out of these three selects entirely, and reading the
          answer off the selects would silently lose it. The ratified spec
          table is a different and wider thing: it answers for the car, not
          for the catalog. So the saved vehicle keeps answering the spec line
          and the tile marker until the shopper edits the cascade themselves.
        */
        var saved = UI.savedVehicle();
        this.saved = saved
          ? { year: saved.year, make: saved.make, model: this.modelWithSubmodel(saved), submodel: '' }
          : null;
        if (this.saved) {
          this.selection = { year: this.saved.year, make: this.saved.make, model: this.saved.model };
        }

        this.bindFields();
        this.initPanels();
        this.buildOptions();
        this.paint();
        // No cookie write on arrival: nothing has been chosen yet.
        this.applyVehicle();

        var clear = this.querySelector('[data-saiko-hub-clear]');
        if (clear) clear.addEventListener('click', this.onClear.bind(this));
      }

      // The convermax rows bake the submodel into the model ("Civic Si"), so
      // a cookie carrying them separately has to be rejoined before it can
      // match a token.
      modelWithSubmodel(vehicle) {
        var model = String(vehicle.model || '');
        var submodel = String(vehicle.submodel || '');
        if (submodel && model.indexOf(submodel) === -1) return (model + ' ' + submodel).trim();
        return model;
      }

      seg(key) {
        return this.querySelector('[data-saiko-hubseg="' + key + '"]');
      }

      select(key) {
        var seg = this.seg(key);
        return seg ? seg.querySelector('select') : null;
      }

      years() {
        return uniqueSorted(this.rows.map(function (row) { return row.year; }), true);
      }

      makes(year) {
        return uniqueSorted(this.rows
          .filter(function (row) { return row.year === year; })
          .map(function (row) { return row.make; }), false);
      }

      models(year, make) {
        return uniqueSorted(this.rows
          .filter(function (row) { return row.year === year && row.make === make; })
          .map(function (row) { return row.model; }), false);
      }

      fill(select, values, placeholder, current) {
        if (!select) return;
        var html = '<option value="">' + placeholder + '</option>';
        values.forEach(function (value) {
          var escaped = String(value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/"/g, '&quot;');
          html += '<option value="' + escaped + '">' + escaped + '</option>';
        });
        select.innerHTML = html;
        select.value = current || '';
      }

      /*
        The lists come from the catalog's own vehicles, so the cookie this
        writes is always a car the rest of the store can actually filter by.
        The one exception is the car that was ALREADY saved: it belongs in
        these controls whether or not we sell parts for it, or the shopper
        reads "All models" directly above a sentence naming their model.
        Merged in place rather than appended, so the ordering holds.
      */
      withSaved(values, savedValue, numeric) {
        if (this.touched || !savedValue || values.indexOf(savedValue) !== -1) return values;
        return uniqueSorted(values.concat([savedValue]), numeric);
      }

      buildOptions() {
        var selection = this.selection;
        var saved = this.touched ? null : this.saved;

        this.fill(
          this.select('year'),
          this.withSaved(this.years(), saved && saved.year, true),
          'All years',
          selection.year
        );
        var yearSelect = this.select('year');
        if (yearSelect && !yearSelect.value) selection.year = '';

        var makeValues = selection.year ? this.makes(selection.year) : [];
        if (saved && selection.year === saved.year) makeValues = this.withSaved(makeValues, saved.make, false);
        this.fill(this.select('make'), makeValues, 'All makes', selection.make);
        var makeSelect = this.select('make');
        if (makeSelect && !makeSelect.value) selection.make = '';

        var modelValues = selection.year && selection.make ? this.models(selection.year, selection.make) : [];
        if (saved && selection.year === saved.year && selection.make === saved.make) {
          modelValues = this.withSaved(modelValues, saved.model, false);
        }
        this.fill(this.select('model'), modelValues, 'All models', selection.model);
        var modelSelect = this.select('model');
        if (modelSelect && !modelSelect.value) selection.model = '';
      }

      bindFields() {
        var self = this;
        ['year', 'make', 'model'].forEach(function (key) {
          var select = self.select(key);
          if (!select || select.dataset.saikoBound === 'true') return;
          select.dataset.saikoBound = 'true';
          select.addEventListener('change', function () {
            // From here the cascade is the truth, not the vehicle that was
            // already saved when the page opened.
            self.touched = true;
            self.selection[key] = select.value;
            // A new year or make invalidates everything downstream rather
            // than carrying a make that year never offered.
            if (key === 'year') {
              self.selection.make = '';
              self.selection.model = '';
            }
            if (key === 'make') self.selection.model = '';
            self.buildOptions();
            self.paint();
            self.handoff();
          });
        });
      }

      complete() {
        return Boolean(this.selection.year && this.selection.make && this.selection.model);
      }

      vehicle() {
        return {
          year: this.selection.year,
          make: this.selection.make,
          model: this.selection.model,
          submodel: ''
        };
      }

      // The car the spec line and the tile marker answer for: the cascade
      // once the shopper has touched it, otherwise whatever was already saved.
      activeVehicle() {
        if (this.complete()) return this.vehicle();
        if (!this.touched && this.saved) return this.saved;
        return null;
      }

      paint() {
        var selection = this.selection;
        var facades = {
          year: selection.year || 'All years',
          make: selection.make || 'All makes',
          model: selection.model || 'All models'
        };
        var locked = { year: false, make: !selection.year, model: !selection.make };

        ['year', 'make', 'model'].forEach(function (key) {
          var seg = this.seg(key);
          if (!seg) return;
          var facade = seg.querySelector('[data-saiko-val]');
          if (facade) facade.textContent = facades[key];
          seg.classList.toggle('is-set', Boolean(selection[key]));
          seg.classList.toggle('is-locked', locked[key]);
        }, this);

        // Also offered when a saved car answers without appearing in these
        // selects, or it could be read but never forgotten.
        var clear = this.querySelector('[data-saiko-hub-clear]');
        if (clear) {
          clear.hidden = !(selection.year || selection.make || selection.model || this.activeVehicle());
        }

        this.paintSpecLine();
      }

      /*
        The wording states the VEHICLE's spec and never claims a product is
        verified for it, which is the same rule the wheels grid and the PDP
        follow. Bolt pattern is the spec where being wrong is a wheel coming
        off, so an unresolved car is told plainly rather than guessed at.
      */
      paintSpecLine() {
        var line = this.querySelector('[data-saiko-hub-spec]');
        if (!line) return;
        while (line.firstChild) line.removeChild(line.firstChild);

        var vehicle = this.activeVehicle();
        if (!vehicle) {
          line.hidden = true;
          return;
        }

        var label = UI.vehicleLabel(vehicle);
        var spec = UI.resolveVehicleSpec(vehicle);
        line.hidden = false;

        if (!spec || (!spec.p && !spec.t)) {
          line.classList.add('saiko-hub-spec--unknown');
          line.appendChild(document.createTextNode(
            'No wheel spec on file for your ' + label
            + ' yet. Pick a bolt pattern below, or text us at (408) 800-6717.'
          ));
          return;
        }

        line.classList.remove('saiko-hub-spec--unknown');
        line.appendChild(document.createTextNode('Your '));
        var car = document.createElement('strong');
        car.textContent = label;
        line.appendChild(car);
        line.appendChild(document.createTextNode(' takes '));

        var parts = [];
        if (spec.p) parts.push(spec.p + ' wheels');
        if (spec.t) parts.push(spec.t + (spec.f === 'bolt' ? ' wheel bolts' : ' lug nuts'));
        parts.forEach(function (text, index) {
          if (index) line.appendChild(document.createTextNode(', '));
          var strong = document.createElement('strong');
          strong.textContent = text;
          line.appendChild(strong);
        });
        if (spec.h) {
          line.appendChild(document.createTextNode(' on a '));
          var hub = document.createElement('strong');
          hub.textContent = spec.h + 'mm';
          line.appendChild(hub);
          line.appendChild(document.createTextNode(' hub'));
        }
        line.appendChild(document.createTextNode('.'));
      }

      /*
        The payoff the ratified table exists for: a completed car sets the
        bolt pattern in the spec bar and marks its own tile. A row that states
        no pattern (the 2002-2003 STI) sets nothing, because saying nothing is
        the honest answer there.
      */
      // Marks the tile and pre-sets the spec bar for whichever car is
      // currently answering. Writes nothing: arrival must not persist.
      applyVehicle() {
        var vehicle = this.activeVehicle();
        paintTiles(vehicle);
        if (!vehicle) return;
        var spec = UI.resolveVehicleSpec(vehicle);
        if (spec && spec.p) setSpecBarPattern(spec.p);
      }

      handoff() {
        if (this.complete()) writeVehicleCookie(this.selection);
        this.applyVehicle();
      }

      onClear(event) {
        event.preventDefault();
        this.selection = { year: '', make: '', model: '' };
        this.touched = true;
        this.saved = null;
        clearVehicleCookie();
        clearSpecBarPattern();
        this.buildOptions();
        this.paint();
        paintTiles(null);
      }

      initPanels() {
        var self = this;
        var fine = window.matchMedia && window.matchMedia('(pointer: fine)').matches;
        if (!fine) return;

        ['year', 'make', 'model'].forEach(function (key) {
          var seg = self.seg(key);
          if (!seg) return;
          var panel = document.createElement('div');
          panel.className = 'saiko-instrument__panel';
          panel.setAttribute('role', 'listbox');
          var label = seg.querySelector('.lbl');
          if (label) panel.setAttribute('aria-label', label.textContent);
          seg.appendChild(panel);

          seg.addEventListener('click', function (event) {
            if (event.target.closest('.saiko-instrument__panel')) return;
            var wasOpen = seg.classList.contains('is-open');
            self.closePanels();
            if (wasOpen || seg.classList.contains('is-locked')) return;
            // Show before building: a display:none panel measures zero, so
            // scrolling it to the current value would silently do nothing.
            seg.classList.add('is-open');
            UI.paintOptionPanel(seg.querySelector('select'), panel);
          });
        });

        document.addEventListener('click', function (event) {
          if (!event.target.closest('saiko-hub-vehicle')) self.closePanels();
        });
        document.addEventListener('keydown', function (event) {
          if (event.key === 'Escape') self.closePanels();
        });
      }

      closePanels() {
        Array.prototype.forEach.call(this.querySelectorAll('.saiko-wheels__seg.is-open'), function (seg) {
          seg.classList.remove('is-open');
        });
      }
    });
  }

  // A saved vehicle marks its tile even when the cascade is not rendered,
  // which is the case whenever the shop token metafield has not been synced.
  UI.whenReady(function () {
    if (document.querySelector('saiko-hub-vehicle')) return;
    paintTiles(UI.savedVehicle());
  });
})();
